import { Component, inject, OnDestroy, OnInit, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';

// Components
import { NavOverflowItem } from '@vg-constellation/angular-19/nav-overflow';
import { PreviewMessageComponent } from '../preview-message/preview-message.component';

// Services
import { AddMessageNotificationService } from '../../services/add-message/add-message-notification.service';
import { ResponsiveBreakpointService } from '../../services/breakpoint/responsive-breakpoint.service';
import { SelectedMessageDetailService } from '../../services/selected-message-detail/selected-message-detail.service';
import { StatementMessagingHttpClientService } from '../../services/http-bridge/statement-messaging-http-client.service';
import { FundDataService } from '../../services/fund-id/fund-id.service';

// Models
import {
  AddUpdateDeleteMessageModel,
  AddUpdateDeleteResponseModel,
  StatementMessageModel,
  StatementMessagingMessageModel,
  TextMessageModel,
} from '../../core/interfaces/statement-messaging-run.model';
import { FundIdWithName } from '../../core/interfaces/fund.model';

// Guards
import { ComponentWithUnsavedChanges } from '../../core/guards/add-message-unsaved-form.guard';

// Constants
import {
  DELETE_VIEW_MESSAGE_ACTION,
  PREVIEW_VIEW_MESSAGE__ACTION,
  PRINT_VIEW_MESSAGE_ACTION,
} from '../../core/constants/statement-messaging.constant';

// Utilities
import {
  getFundCodeByLabel,
  getFundCodeLabel,
  getFundCodeLabels,
} from '../../core/utility/fund-code-enum.util';
import {
  getMessageCodeByKey,
  getMessageLimits,
  getRemainingSpace,
  getTextValidationErrors,
  MessageTypeCode,
  TextWrappingResult,
  validateTextForMessageType,
  wrapTextForMessageType,
} from '../../core/utility/message-enum.util';
import { MessageDataService } from '../../services/message-details/message-details.service';
import {
  AuthorizationService,
  UserPermissions,
} from '../../services/read-write-role/authorization.service';
import { ConfirmSubmitComponent } from '../confirm-submit/confirm-submit.component';

/**
 * Interface for extracted message content
 */
interface ExtractedMessageContent {
  header: string;
  messageText: string;
  messageLines: string[];
}

@Component({
  selector: 'app-view-update-message',
  standalone: false,
  templateUrl: './view-update-message.component.html',
  styleUrl: './view-update-message.component.scss',
})
export class ViewUpdateMessageComponent implements OnInit, OnDestroy, ComponentWithUnsavedChanges {
  // ============================================
  // Constants
  // ============================================
  private readonly PADDING_MESSAGE_ID = 10;
  private readonly ACTION_CODE_UPDATE = 'U';
  private readonly ACTION_CODE_DELETE = 'D';

  // ============================================
  // View Children
  // ============================================
  @ViewChild('confirmationModal') confirmationModal!: ConfirmSubmitComponent;
  @ViewChild('submitConfirmationModal') submitConfirmationModal!: ConfirmSubmitComponent;
  @ViewChild('deleteConfirmationModal') deleteConfirmationModal!: ConfirmSubmitComponent;
  @ViewChild('messageContent') messageContent!: HTMLFormElement;
  @ViewChild('messagePreviewModal') messagePreviewModal!: PreviewMessageComponent;

  @ViewChildren('messageLineInput', { read: ElementRef }) messageLineInputs!: QueryList<ElementRef>;

  // ============================================
  // Public Properties
  // ============================================
  showErrors = false;
  isWaiting = false;
  isDeleteWaiting = false;
  isLoadingMessageText = false;
  bypassSubmit = false;

  selectedMessageDetail: StatementMessageModel | null = null;
  errorMessageWhileFetchingText = '';
  messageTextLines: string[] = [];
  header = '';


  // Fund ID properties from global service
  fundIds: FundIdWithName[] = [];
  isFundIdsLoading = false;
  fundIdsError: string | null = null;

  readonly fundCodeType = getFundCodeLabels();

  // Text Wrapping Properties
  textWrappingResult: TextWrappingResult | null = null;
  wrappedMessageLines: string[] = [];
  textValidationErrors: string[] = [];
  remainingSpace: {
    remainingLines: number;
    remainingCharsOnCurrentLine: number;
    canAddMoreText: boolean;
  } | null = null;

  permissions: UserPermissions = {
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  };

  messageLines: FormControl[] = [];
  maxLinesForCurrentType = 0;
  maxCharsPerLine = 0;

  // ============================================
  // Form
  // ============================================
  formGroup: FormGroup = new FormGroup({
    header: new FormControl(''),
    fund_id: new FormControl(''),
    fund_code: new FormControl(''),
    isPermanent: new FormControl(false),
  });

  // ============================================
  // Services
  // ============================================
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly httpClient = inject(StatementMessagingHttpClientService);
  private readonly notificationService = inject(AddMessageNotificationService);
  private readonly selectedMessageService = inject(SelectedMessageDetailService);
  private readonly messageDataService = inject(MessageDataService);
  private readonly fundDataService = inject(FundDataService);
  protected readonly responsiveService = inject(ResponsiveBreakpointService);
  protected readonly authService = inject(AuthorizationService);

  // ============================================
  // Private Properties
  // ============================================
  private readonly destroy$ = new Subject<void>();
  private formSyncSubscription?: Subscription;
  private isPopulatingForm = false;

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Initialization
  // ============================================

  private initializeSubscriptions(): void {
    this.subscribeToMessageSelection();
    this.subscribeToAuthorization();
    this.subscribeFundIds();
  }

  // ============================================
  // Fund IDs Subscription (from global service)
  // ============================================

  /**
   * Subscribe to fund IDs from global service
   */
  private subscribeFundIds(): void {
    // Subscribe to fund IDs data
    this.fundDataService.fundIds$
      .pipe(takeUntil(this.destroy$))
      .subscribe((fundIds) => {
        this.fundIds = fundIds;
      });

    // Subscribe to loading state
    this.fundDataService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isFundIdsLoading = loading;
      });

    // Subscribe to error state
    this.fundDataService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.fundIdsError = error;
        if (error) {
          // Error state is handled by UI
        }
      });
  }

  /**
   * Retry fetching fund IDs
   */
  retryFetchFundIds(): void {
    this.fundDataService.reload()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fundIds) => {
          this.notificationService.showSuccess(
            `${fundIds.length} fund IDs loaded successfully`,
            'Fund Data Loaded'
          );
        },
        error: (error) => {
          // Error is handled by error$ observable subscription
          console.error('Error retrying fund IDs fetch:', error);
        }
      });
  }

  // ============================================
  // Computed Properties
  // ============================================

  get showFundFields(): boolean {
    if (!this.selectedMessageDetail?.messageTypeCode) {
      return false;
    }

    const fundSpecificCode = getMessageCodeByKey(MessageTypeCode.FUND_SPECIFIC_FULL_PAGE);
    return this.selectedMessageDetail.messageTypeCode === fundSpecificCode;
  }

  get isFundIdDisabled(): boolean {
    return this.isFundIdsLoading || this.fundIds.length === 0 || !!this.fundIdsError;
  }

  get fundIdPlaceholder(): string {
    if (this.isFundIdsLoading) {
      return 'Loading fund IDs...';
    }

    if (this.fundIdsError) {
      return 'Error loading fund IDs';
    }

    if (this.fundIds.length === 0) {
      return 'No fund IDs available';
    }

    return 'Select Fund ID';
  }

  // Text Wrapping Computed Properties
  get messagePreview(): string {
    return this.messageLines
      .map((line: FormControl) => line.value as string || '')
      .join('\n');
  }

  get hasWrappingErrors(): boolean {
    return this.textValidationErrors.length > 0;
  }

  get wrappingStats(): string {
    if (!this.textWrappingResult) {
      return '';
    }

    const stats = this.textWrappingResult.stats;
    return `${stats.totalLines}/${stats.maxLines} lines | Longest: ${stats.longestLine}/${stats.maxCharsPerLine} chars`;
  }

  get remainingSpaceMessage(): string {
    if (!this.remainingSpace) {
      return '';
    }

    if (!this.remainingSpace.canAddMoreText) {
      return 'Maximum lines reached';
    }

    return `${this.remainingSpace.remainingLines} lines remaining | ${this.remainingSpace.remainingCharsOnCurrentLine} chars on current line`;
  }

  get currentLineCount(): number {
    return this.textWrappingResult?.stats.totalLines || 0;
  }

  get maxLineCount(): number {
    return this.textWrappingResult?.stats.maxLines || 0;
  }

  hasAnyLineContent(): boolean{
    return this.messageLines.some((line: FormControl) => line.value && String(line.value).length > 0);
  }

   /**
     * Get message lines for preview modal
     */
    getPreviewMessageLines(): string[] {
      const lines = this.messageLines
        .map((line: FormControl) => line.value as string || '');
      return this.trimTrailingEmptyLines(lines);
    }

    /**
     * Get normalized length for preview display
     * Used in template to show character count with normalized spaces
     */
    getNormalizedLength(value: string | null | undefined): number {
      if (!value) return 0;
      return this.normalizeSpaces(value).length;
    }
  get isNearLineLimit(): boolean {
    if (!this.textWrappingResult) return false;
    const { totalLines, maxLines } = this.textWrappingResult.stats;
    return totalLines >= maxLines - 1;
  }

  /**
   * Normalize multiple consecutive spaces to single space
   * Matches OLD UI behavior for consistent character counts
   */
  private normalizeSpaces(text: string): string {
    return text;
  }

  /**
   * Handle input event with word-boundary wrapping
   * When text exceeds char limit, moves entire words to next line
   * Uses normalized length for calculations but preserves user input
   */
  // eslint-disable-next-line complexity, max-statements
  onMessageLineInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    // Normalize for length calculations only (don't change the actual value yet)
    const normalizedValue = this.normalizeSpaces(currentValue);
    const maxLength = this.maxCharsPerLine;

    // No next line available, just truncate based on normalized length
    if (index >= this.messageLines.length - 1) {
      if (normalizedValue.length > maxLength) {
        // Truncate and normalize when saving
        this.messageLines[index].setValue(this.normalizeSpaces(currentValue.substring(0, maxLength)), { emitEvent: false });
      }
      return;
    }

    // Check if we've exceeded or are at the character limit (based on normalized length)
    if (normalizedValue.length >= maxLength) {
      // Find the last space in the normalized text
      const lastSpaceIndex = normalizedValue.lastIndexOf(' ');

      // Word-boundary wrapping: if there's a space, wrap from there
      if (lastSpaceIndex > 0 && normalizedValue.length > maxLength) {
        // Keep text up to (but not including) the last space on current line
        const textToKeep = normalizedValue.substring(0, lastSpaceIndex);
        // Move the rest (after the space) to next line
        const textToMove = normalizedValue.substring(lastSpaceIndex + 1);

        // Update current line with normalized text (wrapping normalizes)
        this.messageLines[index].setValue(textToKeep, { emitEvent: false });

        // Get next line's current value
        const nextLineValue = this.messageLines[index + 1].value || '';

        // Prepend moved text to next line (with space if next line has content)
        const newNextLineValue = nextLineValue
          ? `${textToMove} ${nextLineValue}`
          : textToMove;

        this.messageLines[index + 1].setValue(newNextLineValue, { emitEvent: false });

        // Focus next input
        this.focusNextInput(index);
      } else if (normalizedValue.length > maxLength) {
        // No space found or single long word, just truncate at max length (and normalize)
        this.messageLines[index].setValue(normalizedValue.substring(0, maxLength), {
          emitEvent: false,
        });
      } else {
        // Exactly at max length, just auto-focus to next line
        // This allows user to continue typing on next line
        this.focusNextInput(index);
      }
    }
  }

  /**
   * Handle keydown event for special keys
   * - Backspace at line start: merge with previous line
   * - Regular keys at max length: auto-focus to next line with word wrapping
   */
  // eslint-disable-next-line complexity, max-statements
  onMessageLineKeydown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // Check if it's a regular character key (not special keys)
    const isRegularKey = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    // Handle regular character at or near max length with word-boundary wrapping
    if (isRegularKey && index < this.messageLines.length - 1) {
      const currentValue = input.value || '';
      const cursorPos = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || 0;
      const hasSelection = cursorPos !== selectionEnd;

      // Simulate what the value would be after this key is typed
      const valueBeforeCursor = currentValue.substring(0, cursorPos);
      const valueAfterCursor = currentValue.substring(selectionEnd);
      const potentialValue = hasSelection
        ? valueBeforeCursor + event.key + valueAfterCursor
        : valueBeforeCursor + event.key + valueAfterCursor;

      // If typing this character would reach or exceed max length, check for word wrapping
      if (potentialValue.length >= this.maxCharsPerLine) {
        // Find last space for word-boundary wrapping
        const lastSpaceIndex = currentValue.lastIndexOf(' ');

        // Only wrap if there's a space (meaning we're in a multi-word context)
        if (lastSpaceIndex > 0) {
          event.preventDefault();

          // Word-boundary wrap: keep text up to (but not including) last space
          const textToKeep = currentValue.substring(0, lastSpaceIndex);
          const textToMove = currentValue.substring(lastSpaceIndex + 1) + event.key;

          this.messageLines[index].setValue(textToKeep, { emitEvent: false });

          // Get next line's current value
          const nextLineValue = this.messageLines[index + 1].value || '';
          const newNextLineValue = nextLineValue
            ? `${textToMove} ${nextLineValue}`
            : textToMove;

          this.messageLines[index + 1].setValue(newNextLineValue, { emitEvent: false });

          // Focus next input and position cursor after the typed character
          setTimeout(() => {
            const inputElements = this.messageLineInputs.toArray();
            const nextInput = inputElements[index + 1];
            const element = nextInput?.nativeElement as HTMLInputElement | undefined;
            if (element) {
              element.focus();
              element.setSelectionRange(textToMove.length, textToMove.length);
            }
          }, 0);
          return;
        } else if (potentialValue.length > this.maxCharsPerLine) {
          // No space and would exceed limit - prevent the character
          event.preventDefault();
          return;
        }
      }
    }

    // Handle backspace at start of line (merge with previous line)
    if (event.key === 'Backspace' && input.selectionStart === 0 && index > 0) {
      event.preventDefault();

      const currentValue = this.messageLines[index].value || '';
      const previousValue = this.messageLines[index - 1].value || '';

      // Only merge if current line has content
      if (!currentValue) {
        // Just focus previous line if current is empty
        setTimeout(() => {
          const inputElements = this.messageLineInputs.toArray();
          const prevInput = inputElements[index - 1];
          const element = prevInput?.nativeElement as HTMLInputElement | undefined;
          if (element) {
            element.focus();
            element.setSelectionRange(previousValue.length, previousValue.length);
          }
        }, 0);
        return;
      }

      // Merge current line content into previous line
      const mergedValue: string = previousValue ? `${previousValue} ${currentValue}` : currentValue;

      // Check if merged content exceeds max length
      if (mergedValue.length > this.maxCharsPerLine) {
        // Apply word-boundary wrapping
        const textUpToLimit: string = mergedValue.substring(0, this.maxCharsPerLine + 1);
        const lastSpaceIndex: number = textUpToLimit.lastIndexOf(' ');

        if (lastSpaceIndex > 0) {
          const textToKeep: string = mergedValue.substring(0, lastSpaceIndex);
          const textToMove: string = mergedValue.substring(lastSpaceIndex + 1);

          this.messageLines[index - 1].setValue(textToKeep, { emitEvent: false });
          this.messageLines[index].setValue(textToMove, { emitEvent: false });
        } else {
          // Can't split, keep as much as possible in previous line
          const prevLineText: string = mergedValue.substring(0, this.maxCharsPerLine);
          const currentLineText: string = mergedValue.substring(this.maxCharsPerLine);
          this.messageLines[index - 1].setValue(prevLineText, { emitEvent: false });
          this.messageLines[index].setValue(currentLineText, { emitEvent: false });
        }
      } else {
        // Merged content fits in previous line
        this.messageLines[index - 1].setValue(mergedValue, { emitEvent: false });
        this.messageLines[index].setValue('', { emitEvent: false });
      }

      // Focus previous line at the position where merge happened
      setTimeout(() => {
        const inputElements = this.messageLineInputs.toArray();
        const prevInput = inputElements[index - 1];
        const element = prevInput?.nativeElement as HTMLInputElement | undefined;
        if (element) {
          element.focus();
          element.setSelectionRange(previousValue.length, previousValue.length);
        }
      }, 0);

      return;
    }
  }

  /**
   * Handle paste event to distribute content across multiple lines
   * Applies word-boundary wrapping for long text
   */
  // eslint-disable-next-line complexity,max-statements
  onMessageLinePaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    if (!pastedText) return;

    const input = event.target as HTMLInputElement;
    const currentValue = input.value || '';
    const cursorPos = input.selectionStart || 0;

    const beforeCursor = currentValue.substring(0, cursorPos);
    const afterCursor = currentValue.substring(input.selectionEnd || cursorPos);

    const pastedLines = pastedText.split(/\r?\n/);

    let currentIndex = index;
    for (let i = 0; i < pastedLines.length && currentIndex < this.messageLines.length; i++) {
      let contentForLine = pastedLines[i];
      if (i === 0) {
        contentForLine = beforeCursor + contentForLine;
      }
      if (i === pastedLines.length - 1) {
        contentForLine += afterCursor;
      }
      this.applyTextWithWrapping(currentIndex, contentForLine);
      currentIndex++;
    }
  }

  /**
   * Handle multi-line paste (when text contains newlines)
   */
  private handleMultiLinePaste(
    startIndex: number,
    beforeCursor: string,
    pastedLines: string[],
    afterCursor: string
  ): void {
    let currentIndex = startIndex;

    for (let i = 0; i < pastedLines.length && currentIndex < this.messageLines.length; i++) {
      const line = pastedLines[i];
      let contentForLine = '';

      if (i === 0) {
        // First line: prepend text before cursor
        contentForLine = beforeCursor + line;
      } else {
        // Subsequent lines: just the pasted content
        contentForLine = line;
      }

      // Last line: append text after cursor
      if (i === pastedLines.length - 1) {
        contentForLine += afterCursor;
      }

      this.applyTextWithWrapping(currentIndex, contentForLine);
      currentIndex++;
    }

    // Focus the last line that received content
    this.focusNextInput(Math.min(currentIndex - 1, this.messageLines.length - 2));
  }

  /**
   * Apply text with word-boundary wrapping across multiple lines
   */
  /**
   * Apply text with word-boundary wrapping across multiple lines
   * Normalizes spaces to match OLD UI wrapping behavior
   */
  private applyTextWithWrapping(startIndex: number, text: string): void {
    // Preserve original spacing (normalize only for character counting, not display)
    let remainingText = text;
    let currentIndex = startIndex;

    while (remainingText.length > 0 && currentIndex < this.messageLines.length) {
      if (remainingText.length <= this.maxCharsPerLine) {
        // Text fits in current line
        this.messageLines[currentIndex].setValue(remainingText, { emitEvent: false });
        break;
      }

      // Text exceeds limit, apply word-boundary wrapping
      const textUpToLimit = remainingText.substring(0, this.maxCharsPerLine + 1);
      const lastSpaceIndex = textUpToLimit.lastIndexOf(' ');

      if (lastSpaceIndex > 0) {
        // Found a space, split at word boundary
        const textToKeep = remainingText.substring(0, lastSpaceIndex);
        this.messageLines[currentIndex].setValue(textToKeep, { emitEvent: false });
        remainingText = remainingText.substring(lastSpaceIndex + 1);
      } else {
        // No space found, truncate at max length
        this.messageLines[currentIndex].setValue(
          remainingText.substring(0, this.maxCharsPerLine),
          { emitEvent: false }
        );
        remainingText = remainingText.substring(this.maxCharsPerLine);
      }

      currentIndex++;
    }
  }

  private focusNextInput(currentIndex: number): void {
    setTimeout(() => {
      const inputElements = this.messageLineInputs?.toArray();
      const nextInput = inputElements?.[currentIndex + 1];
      const element = nextInput?.nativeElement as HTMLInputElement | undefined;
      element?.focus();
    }, 0);
  }

  /**
   * Get character count warning message for a line
   */

  getCharCountMessage(index: number): string{
    const lineValue = this.messageLines[index].value || '';
    // Normalize spaces for character count to match OLD UI behavior
    const normalizedValue = this.normalizeSpaces(lineValue);
    const currentLength = normalizedValue.length;
    const maxLength = this.maxCharsPerLine;

    return `${currentLength}/${maxLength} chars`;
  }
  /**
   * Get remaining characters for a line
   */
  getRemainingChars(index: number): number{
    const lineValue = this.messageLines[index].value || '';
    // Normalize spaces for remaining chars calculation to match OLD UI behavior
    const normalizedValue = this.normalizeSpaces(lineValue);
    return this.maxCharsPerLine - normalizedValue.length;
  }

  /**
   * Check if line is near character limit (15 or less
  remaining)
   */
  isLineNearLimit(index: number): boolean {
    const remaining = this.getRemainingChars(index);
    return remaining <= 15 && remaining >= 0;
  }

  /**
   * Check if line has reached character limit
   */
  isLineLimitReached(index: number): boolean{
    const lineValue = this.messageLines[index].value || '';
    return lineValue.length >= this.maxCharsPerLine;
  }


  /**
   * Get alert class for character count badge
  */

  getCharCountTextClass(index: number): string {
    if (this.isLineLimitReached(index)) {
      return 'text-danger'; // Red when limit reached
    }
    if (this.isLineNearLimit(index)) {
      return 'text-warning'; // Orange when near limit (15 or less)
    }
    return 'text-muted'; // Blue for normal info
  }

  // ============================================
  // Message Selection Subscriptions
  // ============================================

  private subscribeToMessageSelection(): void {
    this.selectedMessageService.selectedMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        if (!message) {
          this.handleNoMessageSelected();
          return;
        }

        this.handleMessageSelected(message);
      });
  }

  private subscribeToAuthorization(): void {
    this.authService.permissions$.pipe(takeUntil(this.destroy$)).subscribe((permissions) => {
      this.permissions = permissions;
    });
  }

  /**
   * Handle message selection
   */
  private handleMessageSelected(message: StatementMessageModel): void {
    this.selectedMessageDetail = message;
    this.fetchMessageData(message.messageId);
  }

  private handleNoMessageSelected(): void {
    this.selectedMessageDetail = null;
    this.resetForm();
  }

  // ============================================
  // Data Fetching
  // ============================================

  private fetchMessageData(messageId: string): void {
    this.prepareForDataFetch();

    this.fetchMessageText(messageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (messageText) => this.handleMessageTextSuccess(messageText),
        error: (error) => this.handleMessageTextError(error),
      });
  }

  private prepareForDataFetch(): void {
    this.isLoadingMessageText = true;
    this.errorMessageWhileFetchingText = '';
    this.disableFormFields();
  }

  private fetchMessageText(messageId: string): Observable<TextMessageModel[]> {
    return this.httpClient.getMessageTextById(messageId).pipe(
      catchError((error) => {
        this.errorMessageWhileFetchingText =
          'We are having some issues while fetching the message text.';
        return of([]);
      }),
    );
  }


  // ============================================
  // Message Text Handling
  // ============================================

  private handleMessageTextSuccess(response: TextMessageModel[]): void {
    this.isLoadingMessageText = false;
    this.enableFormFields();

    if (this.selectedMessageDetail) {
      this.populateFormWithFetchedData(response);
      this.setupFormSync();
    }
  }

  private handleMessageTextError(error: Error): void {
    this.isLoadingMessageText = false;
    this.messageTextLines = [];
    this.header = '';
    this.errorMessageWhileFetchingText =
      'We are having some issues while fetching the message text.';

    this.enableFormFields();

    if (this.selectedMessageDetail) {
      this.populateForm(this.selectedMessageDetail, '');
      this.setupFormSync();
    }
  }

  private populateFormWithFetchedData(response: TextMessageModel[]): void {
    this.unsubscribeFormSync();
    this.isPopulatingForm = true;

    const { header, messageText, messageLines } = this.extractMessageContent(response);

    this.updateFormState(header, messageLines);
    this.patchFormValues(messageText, header);

    this.isPopulatingForm = false;
  }

  private updateFormState(header: string, messageLines: string[]): void {
    this.messageTextLines = messageLines;
    this.header = header;
  }

  /**
   * Patch form values with fund data from global service
   */
  // eslint-disable-next-line complexity
  private patchFormValues(messageText: string, header: string): void {
    const fundIdMatch = this.fundIds.find(
      (fund) => fund.fundPortId === this.selectedMessageDetail?.portId,
    );

    const fundCodeLabel = getFundCodeLabel(
      this.selectedMessageDetail?.displayFundMessageCode || '',
    );

    console.log(' [VIEW-UPDATE] Patching form values:', {
      portId: this.selectedMessageDetail?.portId,
      fundIdsCount: this.fundIds.length,
      fundIdMatch: fundIdMatch
        ? { portId: fundIdMatch.fundPortId, name: fundIdMatch.fundFullName }
        : null,
      fundCodeLabel,
    });

    this.formGroup.patchValue(
      {
        text: messageText,
        header: header || '',
        fund_id: fundIdMatch?.fundPortId || '',
        fund_code: fundCodeLabel,
        isPermanent: this.selectedMessageDetail!.permanentMessageFlag,
      },
      { emitEvent: false },
    );

    this.formGroup.markAsPristine();

    if (fundIdMatch) {
      // Fund ID match found and populated
    } else if (this.selectedMessageDetail?.portId) {
      console.warn(
        ` [VIEW-UPDATE] No matching fund ID found for portId: ${this.selectedMessageDetail.portId}`,
      );
    }
  }

  /**
   * Extract content with text wrapping
   */
  private extractMessageContent(response: TextMessageModel[]): ExtractedMessageContent {
    if (response.length === 0) {
      return { header: '', messageText: '', messageLines: [] };
    }

    if (response.length === 1) {
      return {
        header: response[0].messageText || '',
        messageText: '',
        messageLines: [],
      };
    }

    return this.extractMultiLineContent(response);
  }

  /**
   * Extract content with text wrapping applied
   */
  // eslint-disable-next-line complexity, max-statements
  private extractMultiLineContent(response: TextMessageModel[]): ExtractedMessageContent {
    const header = response[0].messageText || '';

    // Get message type limits and create Form controls
    const messageTypeCode = this.selectedMessageDetail?.messageTypeCode || '';
    const limits = getMessageLimits(messageTypeCode);
    this.maxLinesForCurrentType = limits?.maxLines || 0;
    this.maxCharsPerLine = limits?.maxChars || 0;
    this.createMessageLineControls(limits?.maxLines || 0, limits?.maxChars || 0);

    // populate Form Controls with data from database
    const messageLines = response.slice(1); //skip header
    messageLines.forEach((item, index) => {
      if(index < this.messageLines.length){
        this.messageLines[index].setValue(item.messageText || '', {emitEvent: false})
      }
    });

    // Keep original database lines (preserve exact spacing and line breaks)
    const rawMessageLines = response
      .slice(1)
      .map((item) => item.messageText || '');

    // For validation only: join lines to check against message type limits
    const rawMessageText = rawMessageLines.join(' ');

    // Validate the text (but don't re-wrap existing messages)
    const wrappingResult = wrapTextForMessageType(rawMessageText, messageTypeCode);

    this.textWrappingResult = wrappingResult;

    if (wrappingResult) {
      this.wrappedMessageLines = wrappingResult.lines;
      this.textValidationErrors = wrappingResult.errors;
    } else {
      this.wrappedMessageLines = [];
      this.textValidationErrors = [];
    }

    // Always return the original database lines, not re-wrapped lines
    // This preserves the exact line breaks and spacing from when the message was created
    return {
      header,
      messageText: rawMessageText,
      messageLines: rawMessageLines,
    };
  }

  // ============================================
  // Form Management
  // ============================================

  /**
   * Set up form value change sync with text validation
   */
  private setupFormSync(): void {
    this.unsubscribeFormSync();

    this.formSyncSubscription = this.formGroup.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((formValue) => {
        if (this.shouldSyncForm()) {
          this.syncFormToModel(formValue);
        }
      });
  }

  /**
   * Validate and wrap message text
   */
  private validateAndWrapMessageText(text: string): void {
    if (!text || !this.selectedMessageDetail?.messageTypeCode) {
      this.textWrappingResult = null;
      this.wrappedMessageLines = [];
      this.textValidationErrors = [];
      this.remainingSpace = null;
      return;
    }

    const messageTypeCode = this.selectedMessageDetail.messageTypeCode;

    // Normalize spaces before wrapping for consistent preview display
    const normalizedText = this.normalizeSpaces(text);

    const wrappingResult = wrapTextForMessageType(normalizedText, messageTypeCode);

    if (wrappingResult) {
      this.textWrappingResult = wrappingResult;
      this.wrappedMessageLines = wrappingResult.lines;
      this.textValidationErrors = wrappingResult.errors;
    }

    this.remainingSpace = getRemainingSpace(text, messageTypeCode);
  }

  private shouldSyncForm(): boolean {
    return !!this.selectedMessageDetail && !this.isPopulatingForm;
  }

  private syncFormToModel(formValue: {
    header: string;
    isPermanent: boolean;
    text: string;
    fund_id: string;
    fund_code: string;
  }): void {
    this.selectedMessageDetail = {
      ...this.selectedMessageDetail!,
      messageText: formValue.header,
      permanentMessageFlag: formValue.isPermanent || false,
    };
  }

  private unsubscribeFormSync(): void {
    this.formSyncSubscription?.unsubscribe();
    this.formSyncSubscription = undefined;
  }

  /**
   * Populate form with fund data from global service
   */
  // eslint-disable-next-line complexity
  private populateForm(message: StatementMessageModel, messageText = ''): void {
    this.unsubscribeFormSync();
    this.isPopulatingForm = true;

    const fundIdMatch = this.fundIds.find((fund) => fund.fundPortId === message.portId);
    const fundCodeLabel = getFundCodeLabel(message.displayFundMessageCode || '');

    console.log('🔍 [VIEW-UPDATE] Populating form:', {
      portId: message.portId,
      fundIdsCount: this.fundIds.length,
      fundIdMatch: fundIdMatch
        ? { portId: fundIdMatch.fundPortId, name: fundIdMatch.fundFullName }
        : null,
      fundCodeLabel,
    });

    this.formGroup.patchValue(
      {
        text: messageText,
        header: '',
        fund_id: fundIdMatch?.fundPortId || '',
        fund_code: fundCodeLabel,
        isPermanent: message.permanentMessageFlag,
      },
      { emitEvent: false },
    );

    this.formGroup.markAsPristine();
    this.isPopulatingForm = false;

    if (fundIdMatch) {
      // Fund ID match found and populated
    } else if (message.portId) {
      // No matching fund ID found for port ID
    }
  }

  private createMessageLineControls(maxLines: number, maxChars: number): void{
    this.messageLines = [];
    for(let i = 0; i < maxLines; i++){
      if(i === 0){
        this.messageLines.push(
          new FormControl('', [(control) => Validators.required(control), (control) => Validators.maxLength(maxChars)(control)]));
      }else{
        this.messageLines.push(
          new FormControl('', [(control) => Validators.maxLength(maxChars)(control)]));
      }
    }
  }

  resetForm(): void {
    this.unsubscribeFormSync();
    this.clearFormState();

    if (this.selectedMessageDetail) {
      this.populateForm(this.selectedMessageDetail);
      this.setupFormSync();
    } else {
      this.clearFormValues();
    }

    this.showErrors = false;
    this.isLoadingMessageText = false;
  }

  private clearFormState(): void {
    this.messageLines = [];
    this.messageTextLines = [];
    this.header = '';
    this.errorMessageWhileFetchingText = '';
    this.textWrappingResult = null;
    this.wrappedMessageLines = [];
    this.textValidationErrors = [];
    this.remainingSpace = null;
  }

  private clearFormValues(): void {
    this.formGroup.reset({
      text: '',
      header: '',
      fund_id: '',
      fund_code: '',
      isPermanent: false,
    });
  }

  private disableFormFields(): void {
    this.formGroup.get('text')?.disable();
    this.formGroup.get('header')?.disable();
    this.formGroup.get('fund_id')?.disable();
    this.formGroup.get('fund_code')?.disable();
  }

  private enableFormFields(): void {
    this.formGroup.get('text')?.enable();
    this.formGroup.get('header')?.enable();
    this.formGroup.get('fund_id')?.enable();
    this.formGroup.get('fund_code')?.enable();
  }

  hasFormData(): boolean {
      const values = this.formGroup.value;
      const hasMessageLineData =
      this.messageLines.some((line: FormControl) => line.value &&
      String(line.value).length > 0);
      return !!(
        hasMessageLineData ||
        values.header ||
        values.fund_id ||
        values.fund_code ||
        values.isPermanent
      );
  }

  hasUnsavedChanges(): boolean {
    return this.hasFormData() && !this.isWaiting;
  }

  // ============================================
  // Form Submission
  // ============================================

  // eslint-disable-next-line complexity, max-statements
  submitForm(event: Event): void {
    event.preventDefault();

    // Validate Line 1 is not empty (required field)
    if (this.messageLines.length > 0) {
      const line1Value = this.messageLines[0].value || '';
      if (line1Value.length === 0) {
        this.showErrors = true;
        this.messageLines[0].markAsTouched();
        this.messageLines[0].setErrors({ required: true });
        this.handleInvalidForm(event);
        return;
      }
    }

    if (!this.validateMessageTextBeforeSubmit()) {
      this.handleInvalidForm(event);
      return;
    }

    if (this.formGroup.invalid) {
      this.handleInvalidForm(event);
      return;
    }

    this.submitConfirmationModal.openModalDialog();
  }

  // eslint-disable-next-line complexity
  private validateMessageTextBeforeSubmit(): boolean {
    const messageText = this.formGroup.get('text')?.value || '';
    const messageTypeCode = this.selectedMessageDetail?.messageTypeCode;

    if (!messageTypeCode) {
      return true;
    }

    const isValid = validateTextForMessageType(messageText, messageTypeCode);

    if (!isValid) {
      const errors = getTextValidationErrors(messageText, messageTypeCode);
      this.textValidationErrors = errors;
      this.showErrors = true;

      this.notificationService.showError(errors.join(', '), 'Message text validation failed');
    }

    return isValid;
  }

  private handleInvalidForm(event: Event): void {
    this.showErrors = true;
    this.focusFirstInvalidInput(event);
    this.scrollToFirstError();
  }

  private focusFirstInvalidInput(event: Event): void {
    const target = event.target as HTMLElement;
    const firstInvalid: HTMLElement | null = target?.querySelector('.ng-invalid');
    firstInvalid?.focus();
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      this.document.activeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }

  confirmSubmit(): void {
    this.prepareForSubmission();

    const payload = this.buildUpdateMessagePayload();
    this.httpClient
      .updateDeleteMessage(payload)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: AddUpdateDeleteResponseModel) => {
          this.handleUpdateSuccess(response);
          return this.httpClient.getMessageDetails();
        }),
      )
      .subscribe({
        next: (messages) => this.handleFetchMessagesSuccess(messages),
        error: (error: Error) => this.handleOperationError(error),
      });
  }

  private prepareForSubmission(): void {
    this.isWaiting = true;
    this.bypassSubmit = true;
    this.submitConfirmationModal.closeDialogModal();
    this.messageDataService.setLoading(true);
  }

  cancelSubmit(): void {
    this.submitConfirmationModal.closeDialogModal();
  }

  // ============================================
  // Delete Actions
  // ============================================

  onMessageDelete(): void {
    this.deleteConfirmationModal.openModalDialog();
  }

  cancelDelete(): void {
    this.deleteConfirmationModal.closeDialogModal();
  }

  confirmDelete(): void {
    if (!this.selectedMessageDetail) {
      return;
    }

    this.prepareForDeletion();

    const payload = this.buildDeletePayload();
    this.httpClient
      .updateDeleteMessage(payload)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: AddUpdateDeleteResponseModel) => {
          this.handleDeleteSuccess(response);
          return this.httpClient.getMessageDetails();
        }),
      )
      .subscribe({
        next: (messages) => this.handleFetchMessagesSuccess(messages),
        error: (error: Error) => this.handleOperationError(error),
      });
  }

  private prepareForDeletion(): void {
    this.isDeleteWaiting = true;
    this.bypassSubmit = true;
    this.deleteConfirmationModal.closeDialogModal();
    this.messageDataService.setLoading(true);
  }

  // ============================================
  // Payload Builders
  // ============================================

  private buildDeletePayload(): AddUpdateDeleteMessageModel {
    const formValue = this.formGroup.value;
    const messageTextList = this.buildMessageTextList(formValue);

    const payload: AddUpdateDeleteMessageModel = {
      messageId: this.padString(this.selectedMessageDetail!.messageId, this.PADDING_MESSAGE_ID),
      messageTypeCode: this.selectedMessageDetail!.messageTypeCode,
      messageTextList,
      displayFundMessageCode: this.selectedMessageDetail!.displayFundMessageCode,
      portId: this.selectedMessageDetail!.portId,
      verifyUserId: this.selectedMessageDetail!.verifyUserId,
      actionCode: this.ACTION_CODE_DELETE,
      permanentMessageFlag: this.selectedMessageDetail!.permanentMessageFlag,
    };

    this.addFundFieldsIfNeeded(payload, formValue);

    return payload;
  }

  private buildUpdateMessagePayload(): AddUpdateDeleteMessageModel {
    const formValue = this.formGroup.value;
    const messageTextList = this.buildMessageTextList(formValue);

    const payload: AddUpdateDeleteMessageModel = {
      messageId: this.padString(this.selectedMessageDetail!.messageId, this.PADDING_MESSAGE_ID),
      messageTypeCode: this.selectedMessageDetail!.messageTypeCode,
      messageTextList,
      displayFundMessageCode: null,
      portId: this.selectedMessageDetail!.portId,
      verifyUserId: this.selectedMessageDetail!.verifyUserId,
      actionCode: this.ACTION_CODE_UPDATE,
      permanentMessageFlag: formValue.isPermanent || false,
      messageIdWithoutPadding: this.selectedMessageDetail!.messageId,
    };

    this.addFundFieldsIfNeeded(payload, formValue);

    return payload;
  }

  private buildMessageTextList(formValue: {
    header: string;
    text: string;
    isPermanent: boolean;
    fund_id: string;
    fund_code: string;}): string[]{
      const headerValue: string = formValue.header || '';

      // Build array from message line controls, trimming trailing empty lines
      const lines: string[] = [];
      for(const lineControl of this.messageLines){
        lines.push(lineControl.value || '');
      }

      return [headerValue, ...this.trimTrailingEmptyLines(lines)];
  }

  private trimTrailingEmptyLines(lines: string[]): string[] {
    let end = lines.length;
    while (end > 0 && lines[end - 1].trim() === '') {
      end--;
    }
    return lines.slice(0, end);
  }


  private addFundFieldsIfNeeded(
    payload: AddUpdateDeleteMessageModel,
    formValue: {
      fund_code: string;
      fund_id: string;
      header: string;
      text: string;
      isPermanent: boolean;
    },
  ): void {
    if (this.shouldIncludeFundFields(formValue)) {
      payload.displayFundMessageCode = getFundCodeByLabel(formValue.fund_code) || null;
      payload.portId = formValue.fund_id || null;
    }
  }

  private shouldIncludeFundFields(formValue: {
    fund_code: string;
    fund_id: string;
    header: string;
    text: string;
    isPermanent: boolean;
  }): boolean {
    return this.showFundFields;
  }

  private padString(value: number | string, length: number): string {
    return value.toString().padStart(length, '0');
  }

  // ============================================
  // Response Handlers
  // ============================================

  private handleUpdateSuccess(response: AddUpdateDeleteResponseModel): void {
    this.notificationService.showSuccess(
      `Message ${response.confirmationNumber} updated successfully.`,
      'You successfully updated a message',
    );
  }

  private handleDeleteSuccess(response: AddUpdateDeleteResponseModel): void {
    this.notificationService.showSuccess(
      `Message ${response.confirmationNumber} deleted successfully.`,
      'You successfully deleted a message',
    );
  }

  private handleFetchMessagesSuccess(messages: StatementMessagingMessageModel): void {
    this.messageDataService.setMessageData(messages);
    this.cleanupAndNavigate();
  }

  private handleOperationError(error: Error): void {
    this.notificationService.showError('Operation failed.', 'Please try again later.');
    this.messageDataService.setError(error.message);
    this.cleanupAndNavigate();
  }

  onMenuItemClick(item: NavOverflowItem): void {
    const actionMap = this.getMenuActionMap();
    const action = actionMap.get(item.eventId || '');

    if (action) {
      action();
    }
  }

  private getMenuActionMap(): Map<string, () => void> {
    return new Map([
      [DELETE_VIEW_MESSAGE_ACTION.eventId, () => this.onMessageDelete()],
      [PRINT_VIEW_MESSAGE_ACTION.eventId, () => this.onPrintViewMessageBrowser()],
      [PREVIEW_VIEW_MESSAGE__ACTION.eventId, () => this.onPreviewViewMessage()],
    ]);
  }

  onPreviewViewMessage(): void {
    this.messagePreviewModal.openModal();
  }

  onPrintViewMessageBrowser(): void {
    const formElement: HTMLFormElement = this.messageContent?.nativeElement;

    if (!formElement) {
      return;
    }

    this.activatePrintMode(formElement);
    window.print();
    this.deactivatePrintMode(formElement);
  }

  private activatePrintMode(element: HTMLFormElement): void {
    element.classList.add('printing-active');
  }

  private deactivatePrintMode(element: HTMLFormElement): void {
    setTimeout(() => {
      element.classList.remove('printing-active');
    }, 100);
  }

  onClosePreviewMessage(): void {
    this.messagePreviewModal.closeModal();
  }

  // ============================================
  // Navigation & Cleanup
  // ============================================

  private cleanupAndNavigate(): void {
    this.resetWaitingStates();
    this.clearSelections();
    this.navigateToHome();
  }

  private resetWaitingStates(): void {
    this.isWaiting = false;
    this.isDeleteWaiting = false;
  }

  private clearSelections(): void {
    this.selectedMessageService.clearSelection();
  }

  private navigateToHome(): void {
    this.router.navigate(['/']).then(
      () => console.log('Navigation successful'),
      (error: Error) => console.error('Navigation failed:', error),
    );
  }

  cancelButtonHandler(): void {
    this.navigateToHome();
  }

  private cleanup(): void {
    this.clearFormState();
    this.unsubscribeFormSync();
  }

  // ============================================
  // Unsaved Changes Modal Actions
  // ============================================

  leavePage(): void {
    this.clearSelections();
  }

  stayOnPage(): void {
    // User chose to stay
  }
}
